import { useEffect, useRef, type RefObject } from 'react';
import Hls, { ErrorTypes, type ErrorData, type HlsConfig, type Level } from 'hls.js';
import { usePlayerStore } from '@/store/usePlayerStore';
import { corsFallbackLoaderClass } from './hlsLoader';

/**
 * Module-level reference to the active Hls instance.
 * Used by LiveButton (seek to live) and VideoPlayer (latency).
 * Only one player exists at a time so this is safe.
 */
let activeHls: Hls | null = null;

export function getActiveHls(): Hls | null {
  return activeHls;
}

/**
 * iOS (and iPadOS 13+ that reports as Mac) have no Media Source
 * Extensions support in any browser — they're stuck using the OS
 * native HLS pipeline. Everywhere else we use HLS.js, which means
 * our CorsFallbackLoader (with the /proxy fallback) actually runs.
 *
 * Why not just trust `canPlayType('application/vnd.apple.mpegurl')`?
 * Chrome 105+ reports `"probably"` for that mime type because it has
 * built-in HLS — but Chrome's native HLS doesn't go through our
 * loader, so the CORS proxy never gets a chance. This UA check keeps
 * Chrome on HLS.js where the CORS-fallback chain works.
 */
function isIOSLike(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as Mac with a touch screen.
  if (navigator.maxTouchPoints > 1 && /Mac/.test(ua)) return true;
  return false;
}

function levelToInfo(level: Level) {
  return {
    height: level.height,
    bitrate: level.bitrate,
    name: level.height ? `${level.height}p` : `${Math.round(level.bitrate / 1000)} kbps`,
  };
}

export interface UseHlsResult {
  reload: () => void;
}

/**
 * Attach an HLS stream to a <video> element.
 *
 * Branches:
 *   • iOS / iPadOS — native HLS only (no MSE available).
 *   • Everywhere else (Chrome, Firefox, Edge, macOS Safari, …) —
 *     HLS.js with our CorsFallbackLoader.
 *
 * The Hls instance lives in a ref and survives channel switches:
 * on a URL change we call `hls.loadSource(newUrl)` rather than
 * rebuilding the whole pipeline. This avoids re-binding event
 * listeners, re-creating the MSE source buffer, and the small
 * stutter that comes with it. The instance is only destroyed when
 * the component unmounts.
 *
 * StrictMode-safe: the Hls.destroy() call lives in a separate
 * unmount-only effect so the dev-mode mount→unmount→remount cycle
 * still ends with exactly one live instance per real mount.
 */
export function useHls(
  videoRef: RefObject<HTMLVideoElement | null>,
  url: string | null
): UseHlsResult {
  const hlsRef = useRef<Hls | null>(null);
  const setError = usePlayerStore((s) => s.setError);
  const reloadKey = usePlayerStore((s) => s.reloadKey);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    // ---- iOS / iPadOS: native HLS branch ----
    if (isIOSLike()) {
      // Surface the native video.error event as a player-store error
      // so the UI can show a helpful message (cert error, offline, …).
      const handleNativeError = () => {
        const err = video.error;
        if (!err) return;
        let message = 'Stream failed.';
        switch (err.code) {
          case 1:
            message = 'Stream playback was interrupted.';
            break;
          case 2:
            message =
              'Network error reaching the broadcaster. The stream may be offline or the proxy is unreachable.';
            break;
          case 3:
            message = 'Stream decoding error. The broadcaster may have changed the format.';
            break;
          case 4:
            message =
              "The stream's source isn't supported. The broadcaster may be offline, or its server has an invalid SSL certificate.";
            break;
        }
        setError({ message, type: 'network' });
      };
      video.addEventListener('error', handleNativeError);

      video.src = url;
      // Some Safari versions need an explicit load() to start buffering.
      // Wrap in try/catch in case the URL has no supported source.
      try {
        video.load();
      } catch {
        // Ignore — the error event will surface a meaningful message.
      }

      return () => {
        video.removeEventListener('error', handleNativeError);
        video.removeAttribute('src');
        try {
          video.load();
        } catch {
          // Ignore.
        }
      };
    }

    // ---- HLS.js branch (Chrome, Firefox, Edge, macOS Safari, …) ----
    if (!Hls.isSupported()) {
      setError({
        message:
          'This browser cannot play HLS streams. Try iOS Safari, or a Chromium/Firefox-based browser.',
        type: 'unknown',
      });
      return;
    }

    const config: Partial<HlsConfig> = {
      // CORS-fallback loader. Tries direct URL, falls back to /proxy
      // on CORS / network failure, and rewrites manifest URLs to
      // keep the fallback chain alive for nested requests.
      loader: corsFallbackLoaderClass(),
      startLevel: 0,
      // Live-friendly defaults.
      liveSyncDurationCount: 3,
      maxBufferLength: 30,
      backBufferLength: 30,
      enableWorker: true,
      lowLatencyMode: false,
      liveDurationInfinity: true,
      // Reasonable retry budget; if all retries fail, we surface the error.
      fragLoadingMaxRetry: 3,
      manifestLoadingMaxRetry: 2,
      levelLoadingMaxRetry: 2,
    };

    // Create a fresh Hls instance for each channel switch. Reusing
    // the same MediaSource/SourceBuffer across channels with different
    // codecs causes silent playback failure: data loads over the
    // network but the SourceBuffer rejects the new codec, so the
    // video never renders ("buffering but no picture").
    const hls = new Hls(config);
    hlsRef.current = hls;
    activeHls = hls;

    const { setLevels, setCurrentLevel } = usePlayerStore.getState();

    const handleManifestParsed = () => {
      const levelInfos = (hls.levels || []).map(levelToInfo);
      setLevels(levelInfos);
    };

    const handleLevelSwitched = () => {
      setCurrentLevel(hls.currentLevel);
    };

    const handleError = (_event: typeof Hls.Events.ERROR, data: ErrorData) => {
      if (!data.fatal) {
        // Non-fatal: HLS.js will retry internally.
        return;
      }
      let type: 'cors' | 'network' | 'unknown' = 'unknown';
      let message = 'Stream failed.';

      // HLS.js embeds the upstream HTTP status (when known) into
      // the error message as "status <code>". Pull it out so we can
      // give the user a specific reason for the failure.
      const errMsg = data.error?.message || '';
      const statusMatch = errMsg.match(/status\s+(\d{3})/i);
      const status = statusMatch ? parseInt(statusMatch[1], 10) : 0;
      const text = errMsg.toLowerCase();

      switch (data.type) {
        case ErrorTypes.NETWORK_ERROR:
          type = 'network';
          if (data.details === 'manifestLoadError' || data.details === 'levelLoadError' || data.details === 'fragLoadError') {
            if (text.includes('cert') || text.includes('ssl') || text.includes('tls')) {
              message =
                "This stream's server has an invalid SSL certificate. The broadcaster needs to fix it.";
            } else if (status === 401 || status === 403) {
              message = `The broadcaster returned ${status} Forbidden — the stream is access-restricted (license token, account, or geo-block). The proxy can't bypass this. Pick a different channel.`;
            } else if (status === 400) {
              message =
                'The broadcaster returned 400 Bad Request — the server is rejecting the request. ' +
                'The URL may be expired or the server requires a specific Referer header. ' +
                'Try again or pick a different channel.';
            } else if (status === 404) {
              message = `The broadcaster returned 404 — the stream URL is no longer valid. The channel listing is stale; pick a different one.`;
            } else if (status === 429) {
              message = `The broadcaster is rate-limiting requests. Wait a few seconds and retry.`;
            } else if (status >= 500) {
              message = `The broadcaster's server is having issues (${status}). Try again later, or pick a different stream.`;
            } else {
              message =
                "Couldn't fetch the stream — the broadcaster may be offline, blocking playback, or behind an unsupported CDN. Try a different channel.";
            }
          } else if (data.details === 'manifestParsingError') {
            message =
              "The stream's main playlist couldn't be read — the broadcaster may be offline, " +
              'the URL may be outdated, or the CDN is returning an error page.';
          } else if (data.details === 'levelParsingError') {
            message =
              "A video track playlist couldn't be read — the broadcaster may have changed " +
              'formats, the stream may be down, or a CDN error is blocking it.';
          } else {
            message = `Network error: ${data.details}`;
          }
          break;
        case ErrorTypes.MEDIA_ERROR:
          type = 'network';
          message = `Stream decoding error (${data.details}). The broadcaster may be down or changed format.`;
          break;
        case ErrorTypes.KEY_SYSTEM_ERROR:
          message = 'DRM-protected stream. StreamPitch only handles free HLS feeds.';
          break;
        default:
          message = `Stream error: ${data.details}`;
      }

      setError({ message, type });
    };

    hls.on(Hls.Events.MANIFEST_PARSED, handleManifestParsed);
    hls.on(Hls.Events.LEVEL_SWITCHED, handleLevelSwitched);
    hls.on(Hls.Events.ERROR, handleError);
    hls.attachMedia(video);
    hls.loadSource(url);

    return () => {
      hls.destroy();
      hlsRef.current = null;
      activeHls = null;
    };
  }, [videoRef, url, reloadKey, setError]);

  return {
    reload: () => usePlayerStore.getState().triggerReload(),
  };
}
